/**
 * @fileoverview The Control Panel's route loading bar.
 *
 * Foundry renders `loading-bar__loading-bar` as the FIRST child of
 * `control-panel-shell__settings`, before the sidebar — the DOM diff against
 * the tenant put it at `$root/div[0]` and its absence shifted every sibling
 * after it, which is why one missing element accounted for a large share of the
 * structural differences.
 *
 * IT IS NOT HERE FOR THE DIFF. Adding an element to match a capture, with no
 * behaviour behind it, is cargo-culting — and until this commit there was
 * nothing for it to report, because every settings page was in the entry
 * chunk. The extension registry changed that: eleven pages now arrive as
 * separate chunks, and the gap between clicking a rail item and seeing it is a
 * network fetch. A spinner in the content area answers that by blanking the
 * page you are leaving; a bar at the top of the frame leaves it in place and
 * says work is happening, which is what a route transition should look like.
 *
 * `loading-bar` is its own namespace in the recovered component inventory (4
 * parts, 1 hashed seam), so upstream it is a component package of its own — one
 * bar shared by every app, not a Control Panel widget. Ours is local until a
 * second surface needs it.
 */

import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

import "./LoadingBar.css";

/**
 * How many things are loading. A COUNT and not a boolean, because two
 * overlapping loads that each clear the flag on unmount would hide the bar
 * while the second was still running.
 */
const LoadingContext = createContext<{
  active: number;
  begin: () => void;
  end: () => void;
} | null>(null);

export function LoadingBarProvider({ children }: { children: ReactNode }) {
  const [active, setActive] = useState(0);
  return (
    <LoadingContext.Provider
      value={{
        active,
        begin: () => setActive((n) => n + 1),
        // Floored at zero: a double-end from a remount must not drive the
        // count negative and leave the bar permanently hidden.
        end: () => setActive((n) => Math.max(0, n - 1)),
      }}
    >
      {children}
    </LoadingContext.Provider>
  );
}

/**
 * Marks its lifetime as a load in progress. Rendered as a Suspense fallback, so
 * it mounts when the chunk starts arriving and unmounts when it has.
 *
 * Renders nothing itself — the bar is drawn by `LoadingBar` at the top of the
 * frame, and putting a second indicator in the content area would report the
 * same fact twice.
 */
export function LoadingBarTrigger(): null {
  const ctx = useContext(LoadingContext);
  useEffect(() => {
    if (!ctx) return;
    ctx.begin();
    return ctx.end;
    // Only on mount/unmount: `ctx` is a fresh object each render, and depending
    // on it would begin and end the load on every parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
}

export function LoadingBar(): React.ReactElement {
  const ctx = useContext(LoadingContext);
  const busy = (ctx?.active ?? 0) > 0;
  return (
    <div
      className="loading-bar__loading-bar"
      data-busy={busy || undefined}
      /*
        `role="progressbar"` without a value: this is indeterminate work, and
        announcing a made-up percentage would be worse than announcing none.
        Both attributes drop away when idle, leaving a bare div — which is what
        the tenant renders, and an `aria-hidden` I had added for tidiness showed
        up as a difference against it. An empty div needs no hiding.
      */
      role={busy ? "progressbar" : undefined}
      aria-label={busy ? "Loading" : undefined}
    />
  );
}
